import { ActionBar, Box, Button, Portal } from '@chakra-ui/react'
import { useFloatingBarSpacer } from '@/hooks/useFloatingBarSpacer'

type SettingsActionBarProps = {
  /** Show/hide the bar (usually: dirty && !loading) */
  visible: boolean

  /** Cancel button */
  onCancel: () => void
  cancelLabel?: string
  cancelDisabled?: boolean

  /** Save button */
  onSave: () => void | Promise<void>
  saveLabel?: string
  saveDisabled?: boolean
  saving?: boolean
}

/**
 * The floating save/cancel bar of a settings page. Rendered where the page's content ends: the
 * bar itself floats over the viewport, while a spacer of the bar's height stays in the flow so the
 * last fields can still be scrolled out from under it, and the page scrolls there on its own when
 * the bar appears (SIB-1113).
 */
export function SettingsActionBar({
  visible,
  onCancel,
  cancelLabel = 'Abbrechen',
  cancelDisabled = false,

  onSave,
  saveLabel = 'Änderungen speichern',
  saveDisabled = false,
  saving = false,
}: SettingsActionBarProps) {
  const { barRef, spacerRef, height } = useFloatingBarSpacer(visible)

  if (!visible) return null

  return (
    <>
      <Box
        ref={spacerRef}
        aria-hidden="true"
        data-testid="settings-action-bar-spacer"
        flexShrink={0}
        style={{ height }}
      />
      <ActionBar.Root open>
        <Portal>
          <ActionBar.Positioner>
            <ActionBar.Content ref={barRef}>
              <Button variant="ghost" onClick={onCancel} disabled={cancelDisabled}>
                {cancelLabel}
              </Button>

              <Button
                variant="subtle"
                colorPalette="green"
                onClick={onSave}
                loading={saving}
                disabled={saveDisabled || saving}
              >
                {saveLabel}
              </Button>
            </ActionBar.Content>
          </ActionBar.Positioner>
        </Portal>
      </ActionBar.Root>
    </>
  )
}
